import { createFocusTrap } from 'focus-trap';
import { module } from 'modujs';
import { html } from '../utils/environment';
import Focusable from '../utils/focusable';

export default class extends module {
    constructor(m) {
        super(m);

        this.events = {
            click: {
                'menu-toggler': 'toggleMenu',
                'account-menu-toggler': 'toggleAccountMenu',
                'advisories-menu-toggler': 'toggleAdvisoriesMenu',
                'advisories-menu-close': 'closeAdvisoriesMenu',
            },
        };

        this.$menu = this.$('menu')[0];
        this.$accountMenu = this.$('account-menu')[0];
        this.$advisoriesMenu = this.$('advisories-menu')[0];

        this.$menuToggler = this.$('menu-toggler')[0];
        this.$accountMenuToggler = this.$('account-menu-toggler')[0];
        this.$advisoriesMenuToggler = this.$('advisories-menu-toggler')[0];
        this.$translationsMenuToggler = this.$('translations-menu-toggler-parent')[0];

        this.$menuTogglerParent = this.$('menu-toggler-parent')[0];
        this.$accountMenuTogglerParent = this.$('account-menu-toggler-parent')[0];
        this.$advisoriesMenuTogglerParent = this.$('advisories-menu-toggler-parent')[0];

        this.$headerOverlay = document.getElementById('header-overlay');
    }

    init() {
        this.$headerNavItems = Array.from(this.$('header-nav_item'));
        this.$headerNavItemButtons = Array.from(this.$('header-nav_item_btn'));
        this.headerOverlay = document.getElementById('header-overlay');

        // Focus traps
        if (this.$menu) {
            this.focusTrapMenu = createFocusTrap(
                [
                    this.$menu,
                    this.$headerOverlay,
                    this.$menuTogglerParent,
                    this.$accountMenuTogglerParent,
                    this.$advisoriesMenuTogglerParent,
                    this.$translationsMenuToggler,
                ],
                this.getFocusTrapOptions(
                    this.$menu,
                    'has-menu-opened',
                    this.$menuToggler
                )
            );
        }

        if (this.$accountMenu) {
            this.focusTrapAccount = createFocusTrap(
                [
                    this.$accountMenu,
                    this.$headerOverlay,
                    this.$menuTogglerParent,
                    this.$accountMenuTogglerParent,
                    this.$advisoriesMenuTogglerParent,
                ],
                this.getFocusTrapOptions(
                    this.$accountMenu,
                    'has-account-menu-opened',
                    this.$accountMenuToggler
                )
            );
        }

        if (this.$advisoriesMenu) {
            this.focusTrapAdvisories = createFocusTrap(
                [
                    this.$advisoriesMenu,
                    this.$headerOverlay,
                    this.$menuTogglerParent,
                    this.$accountMenuTogglerParent,
                    this.$advisoriesMenuTogglerParent,
                ],
                this.getFocusTrapOptions(
                    this.$advisoriesMenu,
                    'has-advisories-menu-opened',
                    this.$advisoriesMenuToggler
                )
            );
        }

        this.manageInteractions()
    }

    manageInteractions() {
        this.onHeaderNavItemEnterBind = this.onHeaderNavItemEnter.bind(this)
        this.onHeaderNavItemLeaveBind = this.onHeaderNavItemLeave.bind(this)

        // Using classic event listeners here to prevent `useCapture` behavior
        for (let navItem of this.$headerNavItems) {
            navItem.addEventListener('mouseenter', this.onHeaderNavItemEnterBind)
            navItem.addEventListener('mouseleave', this.onHeaderNavItemLeaveBind)
        }
        for (let navItemButton of this.$headerNavItemButtons) {
            navItemButton.addEventListener('click', this.onHeaderNavItemEnterBind)
        }

        this.onKeyDownBind = (e) => {
            if (e.key == 'Escape') {
                this.closeEverything()
            }
        }
        window.addEventListener('keydown', this.onKeyDownBind)

        this.closeEverythingBind = this.closeEverything.bind(this)
        this.headerOverlay.addEventListener('click', this.closeEverythingBind);
    }

    onHeaderNavItemEnter(e) {
        // DEBUG
        // console.log(e.type, e.currentTarget);

        this.closeEverything();

        // Set current dropdown active
        if (!this.$headerNavItems.includes(e.currentTarget)) {
            e.currentTarget.parentNode.classList.add('is-active')
        } else {
            e.currentTarget.classList.add('is-active')
        }

        // Enable global class on HTML
        html.classList.add('has-header-nav-opened')
    }

    onHeaderNavItemLeave(e) {
        // DEBUG
        // console.log(e.type, e.currentTarget);

        // Set current dropdown inactive
        if (!this.$headerNavItems.includes(e.currentTarget)) {
            e.currentTarget.parentNode.classList.remove('is-active')
        } else {
            e.currentTarget.classList.remove('is-active')
        }

        // Disable global class on HTML
        html.classList.remove('has-header-nav-opened')
    }

    toggleMenu() {
        if (html.classList.contains('has-menu-opened')) {
            this.closeMenu()
        } else {
            this.openMenu()
        }
    }

    openMenu() {
        this.closeEverything();
        this.focusTrapMenu?.activate?.();
    }

    closeMenu() {
        this.focusTrapMenu?.deactivate?.();
    }

    toggleAccountMenu() {
        if (html.classList.contains('has-account-menu-opened')) {
            this.closeAccountMenu()
        } else {
            this.openAccountMenu()
        }
    }

    toggleAdvisoriesMenu() {
        if (html.classList.contains('has-advisories-menu-opened')) {
            this.closeAdvisoriesMenu()
        } else {
            this.openAdvisoriesMenu()
        }
    }

    openAccountMenu() {
        this.closeEverything();
        this.focusTrapAccount?.activate?.();
    }

    closeAccountMenu() {
        this.focusTrapAccount?.deactivate?.();
    }

    openAdvisoriesMenu() {
        this.closeEverything();
        this.focusTrapAdvisories?.activate?.();
    }

    closeAdvisoriesMenu() {
        this.focusTrapAdvisories?.deactivate?.();
    }

    closeEverything() {
        this.closeMenu()
        this.closeAccountMenu()
        this.closeAdvisoriesMenu()

        // Enable global class on HTML
        html.classList.remove('has-header-nav-opened')

        // Disable any other navItem
        for (let navItem of this.$headerNavItems) {
            navItem.classList.remove('is-active')
        }
    }

    /**
     * @param  {Element}  $trap       - The element to trap focus.
     * @param  {string}   activeClass - The active CSS class name.
     * @param  {?Element} [$toggler]  - The element that toggles the trap.
     * @return {Object}  The focus trap options.
     */
    getFocusTrapOptions($trap, activeClass, $toggler = null) {
        return {
            /**
             * There is a delay between when the class is applied
             * and when the element is focusable
             */
            checkCanFocusTrap: (trapContainers) => {
                const results = trapContainers.map((trapContainer) => {
                    return new Promise((resolve) => {
                        const interval = setInterval(() => {
                            if (getComputedStyle(trapContainer).visibility !== 'hidden') {
                                resolve();
                                clearInterval(interval);
                            }
                        }, 5);
                    });
                });

                // Return a promise that resolves when all the trap containers are able to receive focus
                return Promise.all(results);
            },
            onActivate: () => {
                html.classList.add(activeClass);
                $trap.setAttribute('aria-hidden', false);
            },
            onPostActivate: () => {
                if ($toggler) {
                    $toggler.setAttribute('aria-expanded', true);
                }
            },
            onDeactivate: () => {
                html.classList.remove(activeClass);
                $trap.setAttribute('aria-hidden', true);
            },
            onPostDeactivate: () => {
                if ($toggler) {
                    $toggler.setAttribute('aria-expanded', false);
                }
            },
            setReturnFocus: $toggler,
            initialFocus: Focusable($trap).firstFocusable,
        };
    }

    destroy() {
        window.addEventListener('keydown', this.onKeyDownBind)
        this.headerOverlay.removeEventListener('click', this.closeEverythingBind);

        for (let navItem of this.$headerNavItems) {
            navItem.removeEventListener('mouseenter', this.onHeaderNavItemEnterBind)
            navItem.removeEventListener('mouseleave', this.onHeaderNavItemLeaveBind)
        }

        for (let navItemButton of this.$headerNavItemButtons) {
            navItemButton.removeEventListener('click', this.onHeaderNavItemEnterBind)
        }

        this.focusTrapMenu?.deactivate?.({
            returnFocus: false
        });
        this.focusTrapAccount?.deactivate?.({
            returnFocus: false
        });
        this.focusTrapAdvisories?.deactivate?.({
            returnFocus: false
        });
    }
}
